import "../multiplayer-lobby/multiplayer-lobby.scss";

import { useUser } from "@auth0/nextjs-auth0/client";
import { Box, Button, ColorPicker, Popover, Tooltip } from "@mantine/core";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsFillPeopleFill } from "react-icons/bs";

import cardStyles from "@/components/general/card/card.module.css";
import { useSocketContext } from "@/context/socket-context-provider";
import { useUserContext } from "@/context/user-context-provider";

const MultiplayerLobby = ({ roomUrl, price, id }) => {
    const { user: session } = useUser();
    const { t } = useTranslation();
    const { lobbyId, setLobbyId, setLobbyUsers, lobbyUsers, socket, updatePlayerAppearance, setLobbySolved, setLobbyPickedup } =
        useSocketContext();

    const router = useRouter();
    const [opened, setOpened] = useState(false);
    const [newName, setNewName] = useState("Gast");
    const [newColor, setNewColor] = useState("");
    const { user } = useUserContext();

    const canOpenLobby = (user?.escaperooms.includes(id) || price === 0) && session;
    useEffect(() => {
        setNewName(localStorage?.getItem("playerName") || (session ? session.name : "Gast"));
        let newColor = localStorage?.getItem("playerColor");
        if (!newColor) {
            newColor = getRandomColor();
            localStorage.setItem("playerColor", newColor);
        }
        setNewColor(newColor);
    }, [session]);
    const getRandomColor = () =>
        "#" +
        Math.floor(Math.random() * 16_777_215)
            .toString(16)
            .padStart(6, "0");

    const searchParams = useSearchParams();
    const lobbyParam = searchParams.get("lobby");
    useEffect(() => {
        const connectToLobby = async () => {
            if (lobbyParam && socket) {
                const myInfo = { name: newName, color: newColor };

                if (lobbyUsers?.some((user) => user.id === socket.id)) {
                    return;
                }
                socket.emit("joinLobby", lobbyParam, myInfo, (cb, err) => {
                    if (err) {
                        router.push("/");
                    } else {
                        setLobbyId(cb.lobbyId);
                        setLobbyUsers(cb.users);
                        if (cb.pickedup) {
                            setLobbyPickedup(cb.pickedup);
                        }
                        if (cb.solved) {
                            setLobbySolved(cb.solved);
                        }
                    }
                });
            }
        };

        connectToLobby();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lobbyParam, router, session, setLobbyId, lobbyUsers, setLobbyUsers, socket]);
    const openLobby = () => {
        if (socket && session && !lobbyId) {
            const currentUser = { name: newName, color: newColor };
            socket.emit("createLobby", currentUser, (cb) => {
                setLobbyId(cb.lobbyId);
                setLobbyUsers(cb.users);
                navigator.clipboard.writeText("https://online-escaperoom.de" + "/details/" + roomUrl + "?lobby=" + cb.lobbyId);
                router.push(`?lobby=${cb.lobbyId}`);
            });
        } else if (lobbyId) {
            navigator.clipboard.writeText("https://online-escaperoom.de" + "/details/" + roomUrl + "?lobby=" + lobbyId);
            router.push(`?lobby=${lobbyId}`);
        }
    };

    return (
        <Box className={`${cardStyles.card} multiplayer`}>
            <div className="multiplayer-button-container">
                <Tooltip
                    disabled={lobbyId || canOpenLobby}
                    label={
                        lobbyId
                            ? `${t("details.multiplayer.linkTooltip")} ${
                                  "https://online-escaperoom.de" + "/details/" + roomUrl + "?lobby=" + lobbyId
                              } `
                            : !session
                              ? t("details.multiplayer.loginTooltip")
                              : t("yourRooms.noRoom")
                    }>
                    <Button
                        disabled={!lobbyId && !canOpenLobby}
                        onClick={() => {
                            if (lobbyId) {
                                navigator.clipboard.writeText("https://online-escaperoom.de" + "/details/" + roomUrl + "?lobby=" + lobbyId);
                            } else {
                                openLobby();
                            }
                        }}>
                        {lobbyId ? t("details.multiplayer.coppyLink") : t("details.multiplayer.openLobby")}
                    </Button>
                </Tooltip>
                {lobbyId && (
                    <div className="multiplayer-playerCount">
                        <span className="multiplayer-playerCount-number">{lobbyUsers ? lobbyUsers.length : 1}/4</span>
                        <BsFillPeopleFill className="multiplayer-playerCount-image" />
                    </div>
                )}
            </div>

            <div className="multiplayer-user-list">
                {lobbyUsers?.map((user, index) => (
                    <div key={user.id} className="multiplayer-user-list-entry">
                        <span className="multiplayer-user-list-entry-player-number">{index + 1 + "."}</span>
                        {user.id === socket.id ? (
                            <input
                                type="text"
                                className="multiplayer-user-list-entry-player-name-input"
                                placeholder={t("details.multiplayer.namePlaceholder")}
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                onBlur={(e) => {
                                    const updatedName = e.target.value;
                                    setNewName(updatedName);
                                    localStorage.setItem("playerName", updatedName);
                                    updatePlayerAppearance({ name: updatedName, color: newColor });
                                }}
                            />
                        ) : (
                            <span className="multiplayer-user-list-entry-player-name">{user.name}</span>
                        )}
                        {user.id === socket.id ? (
                            <Popover opened={opened} onChange={setOpened} position="bottom" withArrow radius="md" shadow="xl">
                                <Popover.Target>
                                    <span
                                        className="multiplayer-user-list-entry-player-color-input"
                                        onClick={() => setOpened((o) => !o)}
                                        style={{
                                            backgroundColor: newColor,
                                        }}
                                    />
                                </Popover.Target>
                                <Popover.Dropdown
                                    style={{
                                        backgroundColor: "#1a1a1a",
                                        padding: "16px",
                                        borderRadius: "12px",
                                    }}>
                                    <ColorPicker
                                        value={newColor}
                                        onChange={setNewColor}
                                        format="hex"
                                        swatches={[
                                            "#2e2e2e",
                                            "#868e96",
                                            "#fa5252",
                                            "#e64980",
                                            "#be4bdb",
                                            "#7950f2",
                                            "#4c6ef5",
                                            "#228be6",
                                            "#15aabf",
                                            "#12b886",
                                            "#40c057",
                                            "#82c91e",
                                            "#fab005",
                                            "#fd7e14",
                                        ]}
                                    />
                                </Popover.Dropdown>
                            </Popover>
                        ) : (
                            <span className="multiplayer-user-list-entry-player-color" style={{ backgroundColor: user.color }} />
                        )}
                    </div>
                ))}

                {/* Placeholder-Slots */}
                {Array.from({ length: 4 - (lobbyUsers?.length ?? 0) }).map((_, i) => {
                    const playerNumber = (lobbyUsers?.length ?? 0) + i + 1;
                    return (
                        <div key={`placeholder-${playerNumber}`} className="multiplayer-user-list-entry">
                            <span className="multiplayer-user-list-entry-player-number">{playerNumber}.</span>
                        </div>
                    );
                })}
            </div>
        </Box>
    );
};

export default MultiplayerLobby;
